"use client";
import { InputMask } from "@react-input/mask";
import clsx from "clsx";
import type { InputHTMLAttributes, ReactNode } from "react";
import { type Control, type FieldValues, type Path, useController } from "react-hook-form";

type Props<T extends FieldValues> = {
  label: string;
  name: Path<T>;
  control: Control<T>;
  className?: string;
  inputClassName?: string;
  icon?: ReactNode;
  inverse?: boolean;
} & InputHTMLAttributes<HTMLInputElement>;
export const DateInput = <T extends FieldValues>({
  id,
  label,
  name,
  control,
  className,
  inputClassName,
  icon,
  inverse = false,
  ...props
}: Props<T>) => {
  const {
    field: { onBlur, onChange, ref, value },
    fieldState: { error },
  } = useController({ name, control });

  return (
    <div className={clsx("flex flex-col gap-1", className)}>
      {label && (
        <label htmlFor={id} className="text-xs/[14px] pl-2">
          {label}
        </label>
      )}
      <div className="relative">
        <InputMask
          id={id}
          mask="jj/mm/aaaa"
          placeholder="jj/mm/aaaa"
          replacement={{ j: /\d/, m: /\d/, a: /\d/ }}
          showMask={true}
          value={value || ""}
          onChange={onChange}
          onBlur={onBlur}
          ref={ref}
          className={clsx(
            "w-full h-12 transition-colors hover:bg-bg-1/50 focus:bg-white focus:border-tx-2 outline-none rounded-lg border px-4 text-base/5 font-normal placeholder:text-tx-2 placeholder:font-normal placeholder:text-base/5",
            inputClassName,
            {
              "border-no bg-no/[0.02]": error,
              "border-bg-2": !error,
              "bg-bg-1": !error && !inverse,
              "bg-white": !error && inverse,
              "pr-12": icon,
            },
          )}
          {...props}
        />
        {icon && <div className="absolute top-[18px] right-4">{icon}</div>}
      </div>
      {error?.message && <p className="text-no text-xs/[14px] pl-2">{error.message}</p>}
    </div>
  );
};
